import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { Container, DocumentsBlock, DocumentsContainer, formFieldStyles } from "./styles";
import AddNewDocument from "../../../containers/AddNewDocument/AddNewDocument";
import AttachedDocuments from "../../../containers/AttachedDocuments/AttachedDocuments";
import { DTOModel } from "../../../models/base/dto-model";
import { STOModel } from "../../../models/base/sto-model";
import { CompanyLegalEntityRef } from "../../../models/company-accounts/company-legal-entities";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { DocumentDTO } from "../../../models/documents";
import { TransactionDTO } from "../../../models/transactions";
import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";

import { TextFieldTypeAmount } from "../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";
import { BaseApiService } from "../../../services/base";
import { CompanyLegalEntitiesApiService } from "../../../services/company-legal-entities/company-legal-entities-api.service";
import { CurrenciesApiService } from "../../../services/currencies/currencies-api.service";
import { TransactionTypesApiService } from "../../../services/transaction-types/transaction-types-api.service";

import { isValidDate } from "../../../utils/time-utils";
import { DocumentsType } from "../AddEditTransaction/interfaces";
import TransactionContext from "../transaction-context/transaction-context";

interface Props {
  transaction: TransactionDTO;
  handleChangeField: (field: keyof TransactionDTO, value: TransactionDTO[keyof TransactionDTO]) => void;
  handleChangeDocument: (documents: DocumentsType) => void;
  handleChangeDocumentToLink: (documentToLink: DocumentDTO | null) => void;
  legalEntity?: CompanyLegalEntityRef;
  date?: Date;
  documents: DocumentsType;
  documentToLink: DocumentDTO | null;
  disableType: boolean;
  defaultTransaction?: TransactionDTO;
  customDisabledFields?: Partial<keyof TransactionDTO>[];
}

interface FormField {
  labelKey: string;
  field: keyof TransactionDTO;
  type?: "date" | "number";
  apiService?: BaseApiService<DTOModel, STOModel>;
  fieldType: "dropdown" | "textfield";
}

const TransactionScreenOne = ({
  transaction,
  handleChangeField,
  handleChangeDocument,
  handleChangeDocumentToLink,
  legalEntity,
  date,
  documents,
  documentToLink,
  disableType,
  defaultTransaction,
  customDisabledFields,
}: Props) => {
  const { t, i18n } = useTranslation();
  const tRP = "components.add_transaction.transaction_screen_one"; // tRP - translation root path
  const theme = useTheme();
  const typeOfTransactions = useContext(TransactionContext).typeOfTransactions;

  const formFields: FormField[] = [
    {
      labelKey: "common_words.legal_entity",
      field: "legalEntity",
      apiService: new CompanyLegalEntitiesApiService(),
      fieldType: "dropdown",
    },
    {
      labelKey: `${tRP}.formfields.transaction_date`,
      field: "transactionDate",
      type: "date",
      fieldType: "textfield",
    },
    {
      labelKey: `${tRP}.formfields.transaction_type`,
      field: "type",
      apiService: new TransactionTypesApiService(),
      fieldType: "dropdown",
    },
    {
      labelKey: "common_words.amount",
      field: "amount",
      fieldType: "textfield",
      type: "number",
    },
    {
      labelKey: "common_words.comment",
      field: "comment",
      fieldType: "textfield",
    },
  ];

  return (
    <Container sx={{ width: "300px", minWidth: "300px" }}>
      {formFields.map(({ fieldType, type, labelKey, field, apiService }, idx) =>
        fieldType === "textfield" ? (
          field === "amount" ? (
            <React.Fragment key={idx}>
              <TextFieldTypeAmount
                field="amount"
                labelKey={`${t(labelKey)}`}
                handleChange={(_, number) => handleChangeField("amount", number)}
                amountValue={transaction[field] ?? 0}
                sxProps={{
                  ...formFieldStyles,
                  width: "100%",
                  "& .MuiCircularProgress-root": {
                    display: "none",
                  },
                  "& .MuiInputBase-root": {
                    padding: "0px",
                  },
                }}
                endAdornment={
                  <AutocompleteDropdown
                    labelKey={`${t("common_words.currency")}`}
                    propsItem={transaction}
                    field={"currency"}
                    handleChange={handleChangeField}
                    serviceApi={new CurrenciesApiService()}
                    noLabel={true}
                    inputStyle={{
                      width: "100px",
                    }}
                    fieldProp={"code"}
                  />
                }
                required
                disabled={!!customDisabledFields?.includes("amount")}
              />
              {/* <TextField
                key={idx}
                sx={{
                  ...formFieldStyles,
                  width: "100%",
                  "& .MuiCircularProgress-root": {
                    display: "none",
                  },
                }}
                type={type}
                onChange={(e) => handleChangeField("amount", e.target.value)}
                onBlur={(e) => handleChangeField("amount", Number(e.target.value).toFixed(2))}
                variant="outlined"
                label={`${t(labelKey)}`}
                value={transaction[field]}
                inputProps={{
                  step: 0.1,
                  inputMode: "numeric",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <AutocompleteDropdown
                        field="currency"
                        serviceApi={new CurrenciesApiService()}
                        handleChange={handleChangeField}
                        propsItem={transaction}
                        sxProps={{ width: "150px" }}
                        fieldProp="code"
                      />
                    </InputAdornment>
                  ),
                  style: {
                    paddingRight: "0px",
                  },
                }}
                required={true}
              /> */}
            </React.Fragment>
          ) : field === "comment" ? (
            <React.Fragment key={idx}>
              <TextField
                key={idx}
                sx={{
                  ...formFieldStyles,
                  width: "100%",
                }}
                onChange={(e) => handleChangeField("comment", e.target.value)}
                variant="outlined"
                label={`${t(labelKey)}`}
                value={transaction[field]}
                multiline
                rows={2}
                required={true}
                disabled={!!customDisabledFields?.includes("comment")}
              />
            </React.Fragment>
          ) : (
            <React.Fragment key={idx}>
              {field === "transactionDate" && (
                <DesktopDatePicker
                  value={CustomDate.ToDate(transaction[field])}
                  disabled={
                    typeOfTransactions === "bank-and-cash" || !!customDisabledFields?.includes("transactionDate")
                  }
                  onChange={(value) => {
                    let date = null;
                    if (value && isValidDate(value)) {
                      const thisDate = new Date(value);
                      date = CustomDate.FromDate(thisDate);
                    }
                    handleChangeField(field, isValidDate(value) && value ? date : value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ ...formFieldStyles, width: "100%" }}
                      label={`${t(labelKey)}`}
                      required={true}
                      disabled={!!customDisabledFields?.includes("transactionDate")}
                    />
                  )}
                />
              )}
            </React.Fragment>
          )
        ) : (
          apiService &&
          ((typeOfTransactions === "clientInvoice" || typeOfTransactions === "vendorInvoice" || disableType) &&
          field === "type" ? (
            <React.Fragment key={idx}>
              <AutocompleteDropdown
                labelKey={labelKey}
                field={field}
                propsItem={transaction}
                handleChange={handleChangeField}
                serviceApi={apiService}
                sxProps={formFieldStyles}
                required={true}
                disabled={true}
              />
            </React.Fragment>
          ) : typeOfTransactions === "bank-and-cash" && field === "legalEntity" ? (
            <React.Fragment key={idx}>
              <AutocompleteDropdown
                labelKey={labelKey}
                field={field}
                propsItem={transaction}
                handleChange={handleChangeField}
                serviceApi={apiService}
                sxProps={formFieldStyles}
                required={true}
                disabled={true}
              />
            </React.Fragment>
          ) : (
            <React.Fragment key={idx}>
              <AutocompleteDropdown
                labelKey={labelKey}
                field={field}
                propsItem={transaction}
                handleChange={handleChangeField}
                serviceApi={apiService}
                sxProps={formFieldStyles}
                required={true}
                disabled={!!customDisabledFields?.includes(field)}
              />
            </React.Fragment>
          ))
        )
      )}
      {!!transaction.type && !customDisabledFields?.includes("transactionDocuments") && (
        <DocumentsBlock>
          <DocumentsContainer>
            <AddNewDocument
              isWidget
              openButtonKey="Add Document"
              transaction={transaction}
              handleConfirm={handleChangeDocument}
              allowMarkVerified
              hideAllowMarkVerified
              filesProps={documents.newFiles}
              handleChangeDocumentToLink={handleChangeDocumentToLink}
              documentToLink={documentToLink}
              customNameForDocToLink={"Link already uploaded document"}
              showLinkDocument={true}
            />
            {(documentToLink || documents.document) && (
              <div>
                <FontAwesomeIcon
                  icon={faCheck}
                  color={theme.palette.success.main}
                  size={"2x"}
                />
              </div>
            )}
          </DocumentsContainer>
          {defaultTransaction && defaultTransaction?.transactionDocuments?.length !== 0 && (
            <div style={{ width: "60px" }}>
              <AttachedDocuments
                documentIDs={defaultTransaction.transactionDocuments?.map(({ document }) => document?.id as string)}
                transaction={defaultTransaction}
                count={defaultTransaction.transactionDocuments.length}
                buttonSize="2x"
                isWidget
                documentsNames={defaultTransaction?.transactionDocuments?.map((el) => el?.document?.name as string)}
              />
            </div>
          )}
        </DocumentsBlock>
      )}
    </Container>
  );
};

export default TransactionScreenOne;
